import React, { useState } from 'react';

import CrewMemberAutocompleteSingleV2 from 'common/CrewMemberAutocompleteSingleV2';

import OriginalDriverCushioningInputs from '../inputs/OriginalDriverCushioningInputs';
import PostIncidentDrivingInputs from '../inputs/PostIncidentDrivingInputs';
import ReplacementDepartureLocationInput from '../inputs/ReplacementDepartureLocationInput';
import ReplacementEtdEtaInputs from '../inputs/ReplacementEtdEtaInputs';

import styles from './DriverOutOfHoursReplacementStep.module.css';

/**
 * Leaf step — Driver out of hours + Replacement driver.
 *
 * Layout (v2, redesigned — matches fatigueReplacement plus a cushioning
 * section for the original driver, per Scenario 3):
 *   - "Replacement box" (bordered container):
 *     - Replacement departure location
 *     - Replacement driver (crew autocomplete, capped at 3 rows)
 *     - Replacement ETD / Replacement ETA (each split date + time)
 *   - Post-incident driving (outside the box, split date + time)
 *   - Original driver cushioning parameters (section label + Travelling
 *     start/end, each split date + time)
 *
 * The driver on the cushioning block is locked to the original driver; the
 * payload mapper's `driverOutOfHoursReplacement` case sets `outboundTraveling.crewMemberId`
 * accordingly.
 *
 * @param {import('common/activeElements/modals/FlowModal/flowTypes').FlowNodeComponentProps} props
 */
const DriverOutOfHoursReplacementStep = ({ data, onDraftChange }) => {
	const [replacementDriver, setReplacementDriver] = useState(
		data?.replacementDriverId
			? {
					id: data.replacementDriverId,
					fullName: data?.replacementDriverFullName ?? '',
				}
			: null
	);
	const [driving, setDriving] = useState({
		fromTimestamp: data?.driving?.fromTimestamp ?? '',
	});
	const [location, setLocation] = useState({
		fromCityId: data?.replacementInboundTraveling?.fromCityId ?? '',
		fromCityName: data?.replacementInboundTraveling?.fromCityName ?? '',
	});
	const [etdEta, setEtdEta] = useState({
		fromTimestamp: data?.replacementInboundTraveling?.fromTimestamp ?? '',
		toTimestamp: data?.replacementInboundTraveling?.toTimestamp ?? '',
	});
	const [outboundTraveling, setOutboundTraveling] = useState({
		fromTimestamp: data?.outboundTraveling?.fromTimestamp ?? '',
		toTimestamp: data?.outboundTraveling?.toTimestamp ?? '',
	});

	const emitChange = (patch) => {
		const merged = {
			replacementDriverId: replacementDriver?.id ?? null,
			replacementDriver: replacementDriver ?? null,
			driving,
			location,
			etdEta,
			outboundTraveling,
			...patch,
		};
		onDraftChange?.({
			replacementDriverId: merged.replacementDriverId,
			replacementDriver: merged.replacementDriver,
			driving: merged.driving,
			replacementInboundTraveling: {
				fromCityId: merged.location.fromCityId,
				fromCityName: merged.location.fromCityName,
				fromTimestamp: merged.etdEta.fromTimestamp,
				toTimestamp: merged.etdEta.toTimestamp,
			},
			outboundTraveling: merged.outboundTraveling,
		});
	};

	const handleDriverSelection = (selection) => {
		setReplacementDriver(selection);
		emitChange({
			replacementDriverId: selection?.id ?? null,
			replacementDriver: selection ?? null,
		});
	};

	const handleDriverReset = () => {
		setReplacementDriver(null);
		emitChange({ replacementDriverId: null, replacementDriver: null });
	};

	const handleLocationChange = (nextLocation) => {
		setLocation(nextLocation);
		emitChange({ location: nextLocation });
	};

	const handleEtdEtaChange = (nextEtdEta) => {
		setEtdEta(nextEtdEta);
		emitChange({ etdEta: nextEtdEta });
	};

	const handleDrivingChange = (nextDriving) => {
		setDriving(nextDriving);
		emitChange({ driving: nextDriving });
	};

	const handleOutboundChange = (nextOutbound) => {
		setOutboundTraveling(nextOutbound);
		emitChange({ outboundTraveling: nextOutbound });
	};

	return (
		<div className={styles.stack}>
			<div className={styles.replacementBox} data-testid="replacement-box">
				<ReplacementDepartureLocationInput value={location} onChange={handleLocationChange} />
				<CrewMemberAutocompleteSingleV2
					label="Replacement driver"
					placeholder="Select a driver"
					optionsToDisplay={3}
					subtitlePrefix="ID: "
					defaultValue={replacementDriver}
					onSelection={handleDriverSelection}
					onReset={handleDriverReset}
				/>
				<ReplacementEtdEtaInputs
					value={etdEta}
					incidentTimestamp={data?.incidentTimestamp}
					onChange={handleEtdEtaChange}
				/>
			</div>

			<PostIncidentDrivingInputs
				value={driving}
				incidentTimestamp={data?.incidentTimestamp}
				replacementEta={etdEta.toTimestamp}
				onChange={handleDrivingChange}
			/>

			<OriginalDriverCushioningInputs
				value={outboundTraveling}
				incidentTimestamp={data?.incidentTimestamp}
				onChange={handleOutboundChange}
			/>
		</div>
	);
};

export default DriverOutOfHoursReplacementStep;
